import { useState, useCallback, useRef } from 'react';
import { Node, Edge, XYPosition } from '@xyflow/react';

/**
 * Configuration for proximity-based re-parenting
 */
interface ProximityConfig {
  /** Distance threshold in pixels for proximity detection */
  threshold: number;
  /** Whether to show draft edges during drag */
  showDraftEdges: boolean;
}

/**
 * State for tracking proximity-based re-parenting
 */
interface ProximityState {
  /** Node currently being dragged */
  draggedNode: Node | null;
  /** Target node closest to the dragged node */
  closestNode: Node | null;
  /** Distance to the closest node */
  closestDistance: number | null;
  /** Draft edge to show during drag */
  draftEdge: Edge | null;
  /** Original edges of the dragged node (hidden during drag) */
  hiddenEdges: string[];
}

/**
 * Hook for managing proximity-based re-parenting of nodes
 * 
 * This hook provides:
 * - Proximity detection during node drag
 * - Visual feedback with draft edges
 * - Temporary hiding of existing edges
 * - Re-parenting logic on drop
 */
export function useProximityReparenting(
  nodes: Node[],
  edges: Edge[],
  config: ProximityConfig = { threshold: 150, showDraftEdges: true }
) {
  const [proximityState, setProximityState] = useState<ProximityState>({
    draggedNode: null,
    closestNode: null,
    closestDistance: null,
    draftEdge: null,
    hiddenEdges: [],
  });
  
  const dragStartPos = useRef<XYPosition | null>(null);
  
  /**
   * Calculate distance between two nodes
   */
  const calculateDistance = useCallback((node1: Node, node2: Node): number => {
    const dx = node1.position.x - node2.position.x;
    const dy = node1.position.y - node2.position.y;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);
  
  /**
   * Find the closest node to the given position
   */
  const findClosestNode = useCallback((
    draggedNode: Node,
    allNodes: Node[]
  ): { node: Node | null; distance: number | null } => {
    let closestNode: Node | null = null;
    let minDistance = Infinity;
    
    for (const node of allNodes) {
      // Skip the dragged node itself and its descendants
      if (node.id === draggedNode.id) continue;
      
      // Check if this node is a descendant of the dragged node
      // (to prevent circular dependencies)
      const isDescendant = (nodeId: string, ancestorId: string): boolean => {
        const n = allNodes.find(n => n.id === nodeId);
        if (!n) return false;
        if (n.data?.parentId === ancestorId) return true;
        if (n.data?.parentId) return isDescendant(n.data.parentId, ancestorId);
        return false;
      };
      
      if (isDescendant(node.id, draggedNode.id)) continue;
      
      const distance = calculateDistance(draggedNode, node);
      
      if (distance < minDistance && distance < config.threshold) {
        minDistance = distance;
        closestNode = node;
      }
    }
    
    return {
      node: closestNode,
      distance: closestNode ? minDistance : null,
    };
  }, [calculateDistance, config.threshold]);
  
  /**
   * Create a draft edge for visualization
   */
  const createDraftEdge = useCallback((
    sourceNode: Node,
    targetNode: Node
  ): Edge => {
    return {
      id: `draft-edge-${sourceNode.id}-${targetNode.id}`,
      source: targetNode.id,
      target: sourceNode.id,
      type: 'workflow',
      animated: true,
      style: {
        stroke: '#10b981',
        strokeWidth: 3,
        strokeDasharray: '5,5',
      },
      data: {
        isDraft: true,
      },
    };
  }, []);
  
  /**
   * Get edges that should be hidden during drag
   */
  const getEdgesToHide = useCallback((nodeId: string, allEdges: Edge[]): string[] => {
    return allEdges
      .filter(edge => edge.source === nodeId || edge.target === nodeId)
      .map(edge => edge.id);
  }, []);
  
  /**
   * Handle node drag start
   */
  const handleNodeDragStart = useCallback((event: React.MouseEvent, node: Node) => {
    console.log('🖱️ [ProximityReparenting] Drag started:', node.id);
    
    dragStartPos.current = { x: node.position.x, y: node.position.y };
    
    // Find edges to hide
    const edgesToHide = getEdgesToHide(node.id, edges);
    
    setProximityState({
      draggedNode: node,
      closestNode: null,
      closestDistance: null,
      draftEdge: null,
      hiddenEdges: edgesToHide,
    });
  }, [edges, getEdgesToHide]);
  
  /**
   * Handle node drag (movement during drag)
   */
  const handleNodeDrag = useCallback((event: React.MouseEvent, node: Node) => {
    if (!proximityState.draggedNode) return;
    
    // Find closest node
    const { node: closest, distance } = findClosestNode(node, nodes);
    
    // Create draft edge if close enough
    const draftEdge = closest && config.showDraftEdges
      ? createDraftEdge(node, closest)
      : null;
    
    setProximityState(prev => ({
      ...prev,
      draggedNode: node,
      closestNode: closest,
      closestDistance: distance,
      draftEdge,
    }));
  }, [proximityState.draggedNode, nodes, findClosestNode, config.showDraftEdges, createDraftEdge]);
  
  /**
   * Handle node drag stop (drop)
   * Returns the new parent ID if re-parenting should occur, or null otherwise
   */
  const handleNodeDragStop = useCallback((event: React.MouseEvent, node: Node): {
    shouldReparent: boolean;
    newParentId: string | null;
    movedSignificantly: boolean;
  } => {
    console.log('🖱️ [ProximityReparenting] Drag stopped:', {
      nodeId: node.id,
      closestNode: proximityState.closestNode?.id,
      distance: proximityState.closestDistance,
      threshold: config.threshold,
    });
    
    // Calculate if node was moved significantly
    const movedSignificantly = dragStartPos.current 
      ? Math.sqrt(
          Math.pow(node.position.x - dragStartPos.current.x, 2) +
          Math.pow(node.position.y - dragStartPos.current.y, 2)
        ) > 10 // 10px threshold for "moved"
      : false;
    
    // Check if we should re-parent based on proximity
    const shouldReparent = !!(
      proximityState.closestNode &&
      proximityState.closestDistance !== null &&
      proximityState.closestDistance < config.threshold &&
      movedSignificantly
    );
    
    const newParentId = shouldReparent 
      ? proximityState.closestNode!.id 
      : null;
    
    // Reset state
    setProximityState({
      draggedNode: null,
      closestNode: null,
      closestDistance: null,
      draftEdge: null,
      hiddenEdges: [],
    });
    
    dragStartPos.current = null;
    
    return {
      shouldReparent,
      newParentId,
      movedSignificantly,
    };
  }, [proximityState, config.threshold]);
  
  /**
   * Get modified edges list (with draft edges and hidden edges)
   */
  const getModifiedEdges = useCallback((): Edge[] => {
    let modifiedEdges = edges.filter(edge => 
      !proximityState.hiddenEdges.includes(edge.id)
    );
    
    if (proximityState.draftEdge) {
      modifiedEdges = [...modifiedEdges, proximityState.draftEdge];
    }
    
    return modifiedEdges;
  }, [edges, proximityState.hiddenEdges, proximityState.draftEdge]);
  
  return {
    // State
    proximityState,
    
    // Handlers
    handleNodeDragStart,
    handleNodeDrag,
    handleNodeDragStop,
    
    // Modified edges with draft/hidden logic applied
    modifiedEdges: getModifiedEdges(),
    
    // Utility
    isNodeBeingDragged: (nodeId: string) => proximityState.draggedNode?.id === nodeId,
    isClosestNode: (nodeId: string) => proximityState.closestNode?.id === nodeId,
  };
}

